import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from "@mui/material";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import {
  actionDeliveryApproval,
  deleteDeliveryApproval,
  deleteDeliveryApprovalFile,
  getDeliveryApprovalById,
  getProjectBillingMilestones,
  patchDeliveryApproval,
  submitDeliveryApprovals,
  uploadDeliveryFile,
} from "../../services/contractorService";
import Deliverables from "./components/Deliverables";
import UploadGrid from "./components/UploadGrid";

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#A8B5F8", color: "#fff" },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
  "&.Mui-disabled": { color: "#9CA3AF" },
}));

const SHOP_DRAWINGS_ICON = (
  <ArchitectureOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const BOQS_ICON = (
  <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const MATERIAL_SPECS_ICON = (
  <CategoryOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);

// Server tag (FormData key) ←→ section state slot. Keep these aligned
// with the backend exactly — they're literal form keys, not display
// labels.
const SECTIONS = [
  { key: "shopDrawings", tag: "Shop Drawing", title: "Shop Drawings" },
  { key: "boqs", tag: "Boqs", title: "BOQs" },
  {
    key: "materialSpecs",
    tag: "Material Specs",
    title: "Material Specifications",
  },
];

const SECTION_ICON = {
  shopDrawings: SHOP_DRAWINGS_ICON,
  boqs: BOQS_ICON,
  materialSpecs: MATERIAL_SPECS_ICON,
};

const STATUS_PILL_COLORS = {
  PENDING: { bg: "#FEF3C7", text: "#B45309" },
  APPROVED: { bg: "#DCFCE7", text: "#15803D" },
  REJECTED: { bg: "#FEE2E2", text: "#B91C1C" },
  DRAFT: { bg: "#F1F2F4", text: "#6B7280" },
  ARCHIVE: { bg: "#F1F2F4", text: "#6B7280" },
};

const formatStatusLabel = (status) =>
  String(status || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StatusPill = ({ status }) => {
  const key = String(status || "").toUpperCase();
  const palette = STATUS_PILL_COLORS[key] || {
    bg: "#F1F2F4",
    text: "#374151",
  };
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Inter Tight",
        backgroundColor: palette.bg,
        color: palette.text,
        borderRadius: "999px",
        px: 1.25,
        py: 0.25,
        textTransform: "capitalize",
        lineHeight: 1.6,
      }}
    >
      {formatStatusLabel(status)}
    </Typography>
  );
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography
        sx={{
          fontSize: 12,
          color: "#9CA3AF",
          fontFamily: "Inter Tight",
          mt: 0.25,
          ml: 3.5,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Parses milestone.deliverables which can be JSON-string or array.
const parseDeliverables = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Case-insensitive tag lookup so `"Boqs"`, `"BOQs"`, and
// `"Material Specifications"` all route to the right slot.
const resolveSectionKey = (tag) => {
  const t = String(tag || "").trim().toLowerCase();
  if (t === "shop drawing" || t === "shop drawings") return "shopDrawings";
  if (t === "boq" || t === "boqs") return "boqs";
  if (
    t === "material spec" ||
    t === "material specs" ||
    t === "material specification" ||
    t === "material specifications"
  ) {
    return "materialSpecs";
  }
  return null;
};

// Decide whether a file goes in the PDF tile row or the image row.
// Order of precedence:
//   1. `extension` mime-type hint (only when it's a real, specific type)
//   2. URL suffix (".pdf", ".png", ...)
//   3. Default to IMAGES — the shared /delivery-upload endpoint stores
//      every blob as "application/octet-stream" without sniffing, so a
//      generic extension is the signal that the backend couldn't tell.
//      Per spec, that case should land in the image row.
const classifyFileBucket = (extension, url) => {
  const e = String(extension || "").toLowerCase();
  if (e.includes("pdf")) return "pdfs";
  if (e.startsWith("image/")) return "images";
  const u = String(url || "").toLowerCase();
  if (/\.pdf(\?|$|#)/.test(u)) return "pdfs";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$|#)/.test(u)) return "images";
  return "images";
};

// Split design_files[] from the GET-by-id response into 3 section slots.
// Existing files are stored as { id, url, extension, name } so individual
// deletes can call DELETE /design-files/:id.
const splitDesignFilesByTag = (designFiles) => {
  const out = {
    shopDrawings: { pdfs: [], images: [] },
    boqs: { pdfs: [], images: [] },
    materialSpecs: { pdfs: [], images: [] },
  };
  (Array.isArray(designFiles) ? designFiles : []).forEach((f) => {
    const key = resolveSectionKey(f.tag);
    if (!key) return;
    const url = f.image_url || f.path || f.url;
    const item = {
      id: f.id,
      url,
      extension: f.extension,
      name: String(url || "").split("/").pop() || "file",
    };
    const bucket = classifyFileBucket(f.extension, url);
    out[key][bucket].push(item);
  });
  return out;
};

const isFileObject = (x) => x instanceof File;
const itemId = (x) => (x && !isFileObject(x) && x.id != null ? x.id : null);

export default function RequestForApproval() {
  const { id, milestoneId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ---- routing context ----
  // approvalId may be undefined — in which case Save will POST to create.
  const approvalIdParam = searchParams.get("approval_id");
  const forceViewMode = searchParams.get("view") === "1";
  // Prefer the explicit ?project_id query param (added by the project
  // details flow) and fall back to the route segment for legacy links.
  const projectId = searchParams.get("project_id") || id;
  const proposalId = searchParams.get("proposal_id") || id;

  const [approvalId, setApprovalId] = useState(approvalIdParam || null);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [version, setVersion] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  const [milestone, setMilestone] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [shopDrawings, setShopDrawings] = useState({ pdfs: [], images: [] });
  const [boqs, setBoqs] = useState({ pdfs: [], images: [] });
  const [materialSpecs, setMaterialSpecs] = useState({
    pdfs: [],
    images: [],
  });

  const sectionState = { shopDrawings, boqs, materialSpecs };
  const sectionSetters = {
    shopDrawings: setShopDrawings,
    boqs: setBoqs,
    materialSpecs: setMaterialSpecs,
  };

  // Refs for scroll-into-view on validation failure. The first failing
  // section in serial order (timeline → file sections) wins the scroll.
  const timelineRef = useRef(null);
  const shopDrawingsRef = useRef(null);
  const boqsRef = useRef(null);
  const materialSpecsRef = useRef(null);
  const sectionRefs = {
    shopDrawings: shopDrawingsRef,
    boqs: boqsRef,
    materialSpecs: materialSpecsRef,
  };
  const scrollToRef = (ref) => {
    if (ref?.current?.scrollIntoView) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Read-only when status is anything other than DRAFT, OR when the URL
  // explicitly forces view mode (used by the View Approval CTA).
  const isReadOnly =
    forceViewMode ||
    (!!approvalStatus && approvalStatus.toUpperCase() !== "DRAFT");

  const sectionStateRef = useRef(sectionState);
  sectionStateRef.current = sectionState;

  const loadAll = useCallback(
    async ({ silent = false, approvalIdOverride } = {}) => {
      if (!projectId || !milestoneId) return;
      if (!silent) setLoading(true);
      // Allow callers (like Save after POST) to force a specific
      // approval id when the React state hasn't caught up yet — the
      // useCallback closure still holds the old approvalId.
      const effectiveApprovalId =
        approvalIdOverride != null ? approvalIdOverride : approvalId;
      try {
        // 1. Pull the milestone (with its deliverables JSON) from the
        // project-level billing-milestones endpoint. This is the canonical
        // source for the Timeline section — deliverable_description,
        // sub_activities, and any saved start_date / end_date all live
        // here and persist across approval saves.
        const msRes = await getProjectBillingMilestones(projectId);
        const list = Array.isArray(msRes?.data)
          ? msRes.data
          : Array.isArray(msRes)
            ? msRes
            : [];
        const found = list.find((m) => String(m.id) === String(milestoneId));
        if (found) {
          setMilestone(found);
          // deliverables can come back as a JSON-string. Each entry may
          // already carry start_date / end_date (filled in a prior save)
          // or be empty for a fresh milestone — the form starts empty
          // and the user fills them in.
          const dels = parseDeliverables(found.deliverables).map((d, idx) => ({
            id: d.id || `deliverable-${idx}`,
            deliverable_description: d.deliverable_description || d.name || "",
            start_date: d.start_date || "",
            end_date: d.end_date || "",
            sub_activities: Array.isArray(d.sub_activities)
              ? d.sub_activities
              : [],
          }));
          setDeliverables(dels);
        }

        // 2. If we have an approval id, hydrate file slots from the
        // spec'd single-approval endpoint. Timeline dates already come
        // from the milestone above; we no longer merge activity dates
        // back from the approval.
        if (effectiveApprovalId) {
          try {
            const aRes = await getDeliveryApprovalById(effectiveApprovalId);
            const a = aRes?.delivery_approval || aRes?.data || null;
            const designFiles =
              aRes?.design_files || aRes?.delivery_approval?.design_files || [];
            if (a) {
              setApprovalStatus(a.status || "");
              setVersion(a.version ?? null);
            }
            const split = splitDesignFilesByTag(designFiles);
            setShopDrawings(split.shopDrawings);
            setBoqs(split.boqs);
            setMaterialSpecs(split.materialSpecs);
          } catch (e) {
            console.warn("Failed to hydrate delivery approval", e);
          }
        }
      } catch (e) {
        console.error("Failed to load milestone", e);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [projectId, milestoneId, approvalId],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const backToProject = () => {
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    navigate(
      `/project/details/${proposalId}/billing-milestones?${qs.toString()}`,
    );
  };

  const validateDeliverables = () => {
    const today = new Date().toISOString().split("T")[0];
    for (let i = 0; i < deliverables.length; i += 1) {
      const d = deliverables[i];
      const label =
        d.deliverable_description || d.name || `Deliverable ${i + 1}`;
      if (!d.start_date) {
        toast.error(`Please select a start date for "${label}"`);
        scrollToRef(timelineRef);
        return false;
      }
      if (d.start_date < today) {
        toast.error(`Start date cannot be in the past for "${label}"`);
        scrollToRef(timelineRef);
        return false;
      }
      if (!d.end_date) {
        toast.error(`Please select an end date for "${label}"`);
        scrollToRef(timelineRef);
        return false;
      }
      if (d.end_date < d.start_date) {
        toast.error(`End date must be after start date for "${label}"`);
        scrollToRef(timelineRef);
        return false;
      }
    }
    return true;
  };

  // ---- Save / Submit shared payload builder ----
  // Each pending entry is either a still-uploading File (rare — only
  // happens if the user races Save before the immediate upload resolves)
  // OR a freshly-uploaded URL entry `{url,name,extension}` produced by
  // /delivery-upload. Either way the service helper handles it. We skip
  // items that already have a server-side `id` — those live on the
  // approval already and don't need to be re-sent.
  const buildFilesPayload = () => {
    const out = {};
    SECTIONS.forEach(({ key, tag }) => {
      const st = sectionState[key];
      const arr = [];
      [...(st.pdfs || []), ...(st.images || [])].forEach((p) => {
        if (!p) return;
        if (p.id != null) return; // already attached on the server
        if (isFileObject(p)) {
          arr.push(p);
          return;
        }
        if (p.url || p.image_url || p.path) arr.push(p);
      });
      if (arr.length) out[tag] = arr;
    });
    return out;
  };

  const buildActivities = () =>
    (deliverables || []).map((d) => ({
      deliverable_description: d.deliverable_description || d.name || "",
      start_date: d.start_date || null,
      end_date: d.end_date || null,
    }));

  // Save & Submit are disabled until something has been added: at least
  // one file (existing or pending) OR at least one activity with a date.
  const hasAnyContent = useMemo(() => {
    const fileCount = SECTIONS.reduce((acc, { key }) => {
      const st = sectionState[key];
      const pdfs = Array.isArray(st.pdfs) ? st.pdfs.length : 0;
      const imgs = Array.isArray(st.images) ? st.images.length : 0;
      return acc + pdfs + imgs;
    }, 0);
    const anyActivity = (deliverables || []).some(
      (d) => d.start_date || d.end_date,
    );
    return fileCount > 0 || anyActivity;
  }, [shopDrawings, boqs, materialSpecs, deliverables]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSaveOrSubmit = !isReadOnly && hasAnyContent;

  // ---- Section change handlers ----
  // Every newly-picked File hits POST /api/project/delivery-upload right
  // away — there's no local staging. While the request is in flight the
  // entry stays a `File` so UploadGrid can still preview it; once the
  // URL comes back we swap it in place. Removals of already-attached
  // server files still go through the existing /design-files/:id
  // DELETE so the approval row stays in sync.
  const replaceItemInSlot = (key, slot, predicate, nextItem) => {
    sectionSetters[key]((s) => {
      const arr = (s[slot] || []).map((x) => (predicate(x) ? nextItem : x));
      return { ...s, [slot]: arr };
    });
  };

  const removeItemFromSlot = (key, slot, predicate) => {
    sectionSetters[key]((s) => ({
      ...s,
      [slot]: (s[slot] || []).filter((x) => !predicate(x)),
    }));
  };

  const uploadNewFilesInSlot = async (key, slot, nextArr, prevArr) => {
    const prevSet = new Set(prevArr);
    const newFiles = nextArr.filter(
      (x) => x instanceof File && !prevSet.has(x),
    );
    for (const file of newFiles) {
      try {
        const uploaded = await uploadDeliveryFile(file);
        if (uploaded?.url) {
          replaceItemInSlot(
            key,
            slot,
            (x) => x === file,
            {
              url: uploaded.url,
              // mime_type from the backend wins; fall back to the
              // browser's File.type so the payload always has something.
              mime_type:
                uploaded.mime_type ||
                file.type ||
                file.name?.split(".").pop() ||
                null,
              name: file.name,
              extension:
                uploaded.mime_type ||
                file.type ||
                file.name?.split(".").pop(),
            },
          );
        } else {
          toast.error(`Failed to upload ${file.name || "file"}`);
          removeItemFromSlot(key, slot, (x) => x === file);
        }
      } catch (e) {
        console.error("Upload failed", e);
        toast.error(`Failed to upload ${file.name || "file"}`);
        removeItemFromSlot(key, slot, (x) => x === file);
      }
    }
  };

  // Deletes any server-attached files in `removed`. Each DELETE call
  // returns `{ success, message }` — we toast on logical failures
  // (success === false) and rely on the GET refetch to drive the final
  // state: only files actually missing from the GET response leave
  // local state. This is why the handlers below DON'T optimistically
  // drop server-attached items from state; we keep them visible until
  // the server confirms the delete by omitting them.
  const handleServerDeletes = async (removed) => {
    const serverEntries = removed.filter((item) => itemId(item) != null);
    if (serverEntries.length === 0) return;
    await Promise.all(
      serverEntries.map(async (item) => {
        const id = itemId(item);
        try {
          const res = await deleteDeliveryApprovalFile(id);
          if (res?.success === false) {
            toast.error(res?.message || "Failed to delete file");
          }
        } catch (e) {
          console.warn("Failed to delete server file", id, e);
          toast.error(e?.message || "Failed to delete file");
        }
      }),
    );
    // Refetch so the UI mirrors the server-truth: successfully-deleted
    // files leave state, failed ones reappear in place.
    if (approvalId) {
      await loadAll({ approvalIdOverride: approvalId });
    }
  };

  // Build a "next" array that keeps server-attached removed entries in
  // place — they'll be dropped only after DELETE+GET confirms. New
  // additions are appended; local-only (no id) removals apply right
  // away since there's nothing on the server to confirm against.
  const computeNextKeepingServerDeletes = (prev, nextArr) => {
    const nextSet = new Set(nextArr);
    const removed = prev.filter((p) => !nextSet.has(p));
    const pendingServerRemovals = new Set(
      removed.filter((item) => itemId(item) != null),
    );
    const kept = prev.filter(
      (p) => nextSet.has(p) || pendingServerRemovals.has(p),
    );
    const additions = nextArr.filter((item) => !prev.includes(item));
    return { effectiveNext: [...kept, ...additions], removed };
  };

  const handlePdfsChange = (key) => (nextArr) => {
    const prev = sectionStateRef.current[key].pdfs || [];
    const { effectiveNext, removed } = computeNextKeepingServerDeletes(
      prev,
      nextArr,
    );
    sectionSetters[key]((s) => ({ ...s, pdfs: effectiveNext }));
    // Kick off the upload(s) for any new Files in the background.
    uploadNewFilesInSlot(key, "pdfs", nextArr, prev);
    // DELETE server-attached files in parallel, then refetch the approval.
    handleServerDeletes(removed);
  };

  const handleImagesChange = (key) => (nextArr) => {
    const prev = sectionStateRef.current[key].images || [];
    const { effectiveNext, removed } = computeNextKeepingServerDeletes(
      prev,
      nextArr,
    );
    sectionSetters[key]((s) => ({ ...s, images: effectiveNext }));
    uploadNewFilesInSlot(key, "images", nextArr, prev);
    handleServerDeletes(removed);
  };

  // Branching on approvalId: POST when none, PATCH when present.
  // Returns the resolved approval id so callers (Save) can refetch the
  // GET-by-id before React has flushed the new state.
  const persist = async () => {
    const filesPayload = buildFilesPayload();
    const activities = buildActivities();
    if (approvalId) {
      await patchDeliveryApproval(approvalId, filesPayload, activities);
      return { approvalId };
    }
    const res = await submitDeliveryApprovals(
      milestoneId,
      filesPayload,
      activities,
    );
    const newId =
      res?.delivery_approval?.id ||
      res?.data?.delivery_approval?.id ||
      res?.data?.id ||
      res?.id;
    if (newId != null) {
      setApprovalId(String(newId));
      const status = res?.delivery_approval?.status || res?.data?.status;
      if (status) setApprovalStatus(status);
    }
    return { approvalId: newId != null ? String(newId) : null };
  };

  const handleSave = async () => {
    // Save accepts partial data — users can fill sections incrementally
    // and persist what they've got. The only gate is `canSaveOrSubmit`
    // (must have *something* — at least one file or a touched date),
    // which already disables the button when the entire payload is empty.
    setSaving(true);
    try {
      const { approvalId: persistedId } = await persist();
      toast.success("Approval request saved");
      // Re-pull the approval (GET /delivery-approvals/:id) so the UI
      // reflects the canonical server state — file ids assigned by the
      // backend, version bumps, etc. Pass the resolved id explicitly:
      // the useCallback closure may still hold the pre-POST null.
      await loadAll({ approvalIdOverride: persistedId });
    } catch (e) {
      console.error("Failed to save approval request", e);
      toast.error(e?.message || "Failed to save approval request");
    } finally {
      setSaving(false);
    }
  };

  // True when any section still has a `File` instance — i.e. an upload
  // is still mid-flight via /delivery-upload. We pause Submit briefly so
  // the request doesn't race the upload API.
  const hasPendingFiles = () =>
    SECTIONS.some(({ key }) => {
      const st = sectionState[key];
      if ((st.pdfs || []).some((p) => isFileObject(p))) return true;
      return (st.images || []).some((img) => isFileObject(img));
    });

  // True when any section contains an item that's been uploaded but not
  // yet attached to the approval row (URL entry with no server `id`).
  // Triggers an automatic PATCH right before Submit so the action call
  // sees a fully-saved row.
  const hasUnsavedUploads = () =>
    SECTIONS.some(({ key }) => {
      const st = sectionState[key];
      const lacksId = (arr) =>
        (arr || []).some(
          (x) => x && !isFileObject(x) && x.id == null,
        );
      return lacksId(st.pdfs) || lacksId(st.images);
    });

  // Mandatory-at-submit check: section has at least one file (server
  // entry or freshly-uploaded URL entry).
  const sectionHasAnything = (key) => {
    const st = sectionState[key];
    const pdfs = Array.isArray(st.pdfs) ? st.pdfs.length : 0;
    const imgs = Array.isArray(st.images) ? st.images.length : 0;
    return pdfs + imgs > 0;
  };

  // Click handler — runs validations only. On success, opens the confirm
  // modal; the actual API calls live in `confirmSubmit`.
  // Mandatory before submitting:
  //   - Timeline: every deliverable row must have BOTH start_date and
  //     end_date filled, with start ≤ end and start ≥ today.
  //   - BOQs: at least one file uploaded.
  // Shop Drawings and Material Specs are optional at submit time.
  const handleSubmit = () => {
    if (!approvalId) return; // button is hidden in this state; defensive
    if (hasPendingFiles()) {
      toast.error("Please wait for file uploads to finish before submitting");
      return;
    }
    if (!validateDeliverables()) return;
    if (!sectionHasAnything("boqs")) {
      toast.error("Please upload BOQs");
      scrollToRef(boqsRef);
      return;
    }
    setSubmitConfirmOpen(true);
  };

  const closeSubmitConfirm = () => {
    if (submitting) return;
    setSubmitConfirmOpen(false);
  };

  // Confirm flow — if there are uploaded-but-unattached files, run
  // PATCH first so the approval row has the latest design_file_urls;
  // then PUT /action with status=SUBMITTED. Pure PUT when nothing's
  // dirty.
  const confirmSubmit = async () => {
    if (!approvalId) return;
    setSubmitting(true);
    try {
      if (hasUnsavedUploads()) {
        await persist();
      }
      await actionDeliveryApproval(approvalId, "SUBMITTED");
      toast.success("Approval submitted for review");
      setSubmitConfirmOpen(false);
      backToProject();
    } catch (e) {
      console.error("Failed to submit approval", e);
      toast.error(e?.message || "Failed to submit approval");
    } finally {
      setSubmitting(false);
    }
  };

  // Dialog open/close — confirm flow lives in `confirmDeleteApproval`.
  const openDeleteModal = () => {
    if (!approvalId) return;
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
  };

  const confirmDeleteApproval = async () => {
    if (!approvalId) return;
    setDeleting(true);
    try {
      await deleteDeliveryApproval(approvalId);
      toast.success("Delivery approval deleted");
      setDeleteModalOpen(false);
      backToProject();
    } catch (e) {
      console.error("Failed to delete approval", e);
      toast.error(e?.message || "Failed to delete approval");
    } finally {
      setDeleting(false);
    }
  };

  const milestoneLabel = useMemo(
    () =>
      milestone?.description ||
      milestone?.name ||
      milestone?.group_name ||
      "Request for Approval",
    [milestone],
  );

  // DRAFT-only header CTA to delete the approval entirely.
  const showHeaderDelete =
    !!approvalId &&
    String(approvalStatus || "").toUpperCase() === "DRAFT" &&
    !forceViewMode;

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          gap: "10px",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <LeftArrowIcon onClick={backToProject} style={{ cursor: "pointer" }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              color: "#0D0D0E",
              fontSize: { xs: "15px", md: "16px" },
              fontWeight: 500,
            }}
          >
            {isReadOnly ? "View Approval" : "Request for Approval"} —{" "}
            {milestoneLabel}
          </Box>
          {version != null && (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                backgroundColor: "#F1F2F4",
                borderRadius: "999px",
                px: 1.25,
                py: 0.25,
              }}
            >
              Version-{version}
            </Box>
          )}
          {approvalStatus && <StatusPill status={approvalStatus} />}
        </Box>
        {showHeaderDelete && (
          <Button
            onClick={openDeleteModal}
            disabled={deleting}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            sx={{
              color: "#DC2626",
              border: "1px solid #FECACA",
              borderRadius: "10px",
              backgroundColor: "#fff",
              textTransform: "none",
              fontFamily: "Inter Tight",
              fontWeight: 500,
              fontSize: 14,
              padding: "6px 14px",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#FEF2F2",
                borderColor: "#FCA5A5",
                boxShadow: "none",
              },
            }}
          >
            Delete Approval
          </Button>
        )}
      </Box>

      <Box
        component="fieldset"
        disabled={isReadOnly}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: { xs: "2vh 4vw 2vh", md: "2vh 2vw 2vh 2vw" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          // strip native fieldset chrome
          border: "none",
          margin: 0,
          minWidth: 0,
        }}
      >
        <Box
          ref={timelineRef}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            backgroundColor: "#fff",
            p: 2.5,
            scrollMarginTop: 16,
          }}
        >
          <SectionHeader
            icon={
              <CalendarTodayOutlinedIcon
                sx={{ fontSize: 18, color: "#4D6BF6" }}
              />
            }
            title="Timeline"
          />
          <Deliverables
            deliverables={deliverables}
            onChange={setDeliverables}
          />
        </Box>

        {SECTIONS.map(({ key, title }) => {
          const st = sectionState[key];
          return (
            <Box
              key={key}
              ref={sectionRefs[key]}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                p: 2.5,
                scrollMarginTop: 16,
              }}
            >
              <UploadGrid
                title={title}
                subtitle="Upload images & PDFs"
                icon={SECTION_ICON[key]}
                pdfFiles={st.pdfs}
                onPdfFilesChange={handlePdfsChange(key)}
                images={st.images}
                onImagesChange={handleImagesChange(key)}
                readOnly={isReadOnly}
              />
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          padding: { xs: "12px 4vw", md: "1.5vh 2vw" },
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderTop: { xs: "1px solid #E5E7EB", md: "none" },
        }}
      >
        <SecondaryButton onClick={backToProject}>
          {isReadOnly ? "Back" : "Cancel"}
        </SecondaryButton>
        {!isReadOnly && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <SecondaryButton
              onClick={handleSave}
              disabled={!canSaveOrSubmit || saving || submitting}
            >
              {saving ? "Saving..." : "Save"}
            </SecondaryButton>
            {/* Submit only appears once the approval row exists (POST
                completed at least once). Until then Save is the only
                primary action — there's nothing to submit yet. */}
            {approvalId && (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!canSaveOrSubmit || saving || submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </PrimaryButton>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(420px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Delete Approval?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
            }}
          >
            Are you sure you want to delete this approval details?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeDeleteModal}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              border: "1px solid #D7D8D9",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteApproval}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#fff",
              backgroundColor: "#DC2626",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#B91C1C" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit confirmation — same shape as the delete modal but the
          primary action is blue and the body asks for confirmation
          before firing PATCH (if dirty) + PUT /action. */}
      <Dialog
        open={submitConfirmOpen}
        onClose={closeSubmitConfirm}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(420px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Submit Approval?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
            }}
          >
            Are you sure you want to submit the approval?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeSubmitConfirm}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              border: "1px solid #D7D8D9",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSubmit}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#fff",
              backgroundColor: "#4D6BF6",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#3B5BDB" },
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
